"use client"

import { useEffect, useState } from "react"

/**
 * Per-segment lesson narration. Each caption line (one SRT cue) gets its own
 * TTS clip, played back to back — so the talk bubble is on screen exactly as
 * long as its own audio and the words always match the voice. Same "marin"
 * voice + server-side clip cache as the Missies lessons
 * (public/games/playquest/js/bella.js).
 */

const TTS_URL = "/api/openai-tts.php"
const clips = new Map() // text -> object URL
const inflight = new Map() // text -> Promise

async function requestClip(text) {
  const res = await fetch(TTS_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      text,
      model: "gpt-4o-mini-tts",
      voice: "marin",
      response_format: "mp3",
      speed: 1.0,
    }),
  })
  const ct = res.headers.get("content-type") || ""
  if (!res.ok || !(ct.includes("audio") || ct.includes("mpeg"))) {
    const err = new Error("no-audio:" + res.status)
    err.fatal = res.status === 400
    throw err
  }
  return URL.createObjectURL(await res.blob())
}

async function fetchClip(text) {
  let last = null
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt) await new Promise((r) => setTimeout(r, 300 * attempt))
    try {
      return await requestClip(text)
    } catch (e) {
      last = e
      if (e && e.fatal) break
    }
  }
  throw last || new Error("no-audio")
}

export function clipUrl(text) {
  const t = (text || "").trim()
  if (!t) return Promise.resolve(null)
  if (clips.has(t)) return Promise.resolve(clips.get(t))
  if (inflight.has(t)) return inflight.get(t)
  const p = fetchClip(t).then(
    (url) => {
      clips.set(t, url)
      inflight.delete(t)
      return url
    },
    (err) => {
      inflight.delete(t)
      throw err
    },
  )
  inflight.set(t, p)
  return p
}

/**
 * @param {string[]} segments  the ordered caption lines
 * @param {number} index       which segment is live
 * @param {boolean} enabled    false = skip (video step, controls hidden)
 * @returns {string|null} audio URL for the current segment, or null while it
 *   is still loading (the caller keeps showing the caption meanwhile)
 */
export function useSegmentedNarration(segments, index, enabled = true) {
  const text = enabled && Array.isArray(segments) ? (segments[index] || "").trim() : ""
  const [src, setSrc] = useState(() => (text && clips.get(text)) || null)

  useEffect(() => {
    if (!text) {
      setSrc(null)
      return
    }
    if (clips.has(text)) {
      setSrc(clips.get(text))
    } else {
      let alive = true
      setSrc(null)
      clipUrl(text).then(
        (url) => alive && setSrc(url),
        () => alive && setSrc(null),
      )
      // eslint-disable-next-line consistent-return
      return () => {
        alive = false
      }
    }
  }, [text])

  // Warm the next clip so it starts near-instantly on advance.
  useEffect(() => {
    if (!enabled || !Array.isArray(segments)) return
    const next = (segments[index + 1] || "").trim()
    if (next && !clips.has(next)) clipUrl(next).catch(() => {})
  }, [segments, index, enabled])

  return src
}

export default useSegmentedNarration
